import React from 'react';

import {select} from 'd3-selection';
import {interpolateNumber} from 'd3-interpolate';
import 'metrics-graphics/dist/metricsgraphics.css';
import Measure from 'react-measure';

import {GRID_UNIT} from '../styles';
import {
    formatDistance,
    formatElevation,
    metersToUsersUnits,
    metersToUsersDistanceUnits
} from '../utils/formatters';
import './ElevationProfile.css';

/**
 * ElevationProfile component for showing an interactive graph of selected trails.
 *
 * ---------------------------
 * |           -O-           |
 * |    --  ---   --         |
 * | ---  --        --       |
 * |-                 -------|
 * ----------------------------
 *
 * @param {Object}        props
 * @param {Array[Object]} props.locations - An array of time/elevation tuples.
 * @param {(data: {distance: Number, elevation: Number, line_id: Number}, index: Number): void}
 *      props.onMouseover - A listener to be run on the mouseover event
 * @param {(): void} props.onMouseout - A listener to be run on the mouseout event

 *
 * @return {React.Component}
 */

// constants for sizing the graph as it moves from desktop <-> mobile width
const TRANSFORM_WIDTH = 620;
const WIDE_RIGHT = 10;

class ElevationProfile extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            selectedPoint: null,
            dimensions: {},
            right: WIDE_RIGHT
        };
    }

    componentDidMount() {
        import('metrics-graphics').then(({default: MG}) => {
            this.renderGraph(MG);
        });
    }

    renderGraph(MG) {
        // avoid JS errors, don't render the graph for a non-trail
        if (!this.state.dimensions.width || this.props.elevation == null) {
            return;
        }

        // metrics graphics should infer maxY by default according to the docs,
        // but the graph ends up really flat
        let maxY = -99999;
        // inflate maxX slightly so the final tick shows
        let maxX = -99999;
        const x_accessor = 'formattedDistance';
        const y_accessor = 'formattedElevation';
        const xax_format;
        const xax_count;

        const graphData = addInterpolatedPoints(this.props.elevation.map(point => ({
            formattedDistance: metersToUsersDistanceUnits(point[0]),
            formattedElevation: metersToUsersUnits(point[1]),
            distance: point[0],
            elevation: point[1],
        })), this.state.dimensions.width);

        for (const point of graphData) {
            if (point.formattedElevation > maxY) {
                maxY = point.formattedElevation;
            }
            if (point.formattedDistance > maxX) {
                maxX = point.formattedDistance;
            }
        }
        // so top of graph isn't clipped
        maxY += 1;
        // so the last tick mark with full distance shows
        maxX *= 1.05;

        const graphDict = this.graphInfo(
            graphData,
            maxX,
            maxY,
            x_accessor,
            y_accessor,
            xax_format,
            xax_count,
            'line',
            true
        );
        graphDict.mouseover = (d, i) => {
            if (this.props.elevation != null) {
                let rise;
                let run;
                if (i === graphData.length - 1) {
                    const firstPoint = graphData[i - 1];
                    const nextPoint = d;
                    rise = firstPoint.elevation - nextPoint.elevation;
                    run = firstPoint.distance - nextPoint.distance;
                } else {
                    const firstPoint = d;
                    const nextPoint = graphData[i + 1];
                    rise = firstPoint.elevation - nextPoint.elevation;
                    run = firstPoint.distance - nextPoint.distance;
                }
                const grade = (rise / run * 100).toFixed(1);
                select('svg .mg-active-datapoint')
                    .text(
                        `Distance: ${formatDistance(d.distance)}  Elevation: ${formatElevation(
                            d.elevation
                        )}  Grade: ${grade}%`
                    );
                if (this.props.onMouseover) this.props.onMouseover(d, i);
            }
        };
        graphDict.mouseout = this.props.onMouseout;
        MG.data_graphic(graphDict);
    }

    // eslint-disable-next-line max-params
    graphInfo(
        graphData,
        maxX,
        maxY,
        x_accessor,
        y_accessor,
        xax_format,
        xax_count,
        chart_type,
        min_y_from_data
    ) {
        return {
            chart_type: chart_type,
            bar_margin: 5,
            binned: true,
            bins: 12,
            data: graphData,
            min_y_from_data: min_y_from_data,
            max_y: maxY,
            max_x: maxX,
            width: this.state.dimensions.width,
            height: 120,
            left: GRID_UNIT * 12,
            right: this.state.right,
            top: GRID_UNIT * 4,
            bottom: GRID_UNIT * 6,
            area: false,
            color: '#fff',
            target: this.target,
            x_accessor: x_accessor,
            y_accessor: y_accessor,
            xax_format: xax_format,
            xax_count: xax_count,
        };
    }

    render() {
        return (
            <Measure
                onMeasure={dimensions => {
                    const right = window.innerWidth <= TRANSFORM_WIDTH ? 5 : WIDE_RIGHT;
                    this.setState({dimensions, right});
                }}
            >
                <div
                    ref={ref => {
                        this.target = ref;
                    }}
                />
            </Measure>
        );
    }
}

// Add interpolated points to `input` in order to create a set with `count` points
function addInterpolatedPoints(input, count) {
    const output = [];
    let inputIndex = 0;
    const interpolatedCount = count - input.length;

    let i = 0;
    while (i < interpolatedCount) {
        const distance = input[input.length - 1].distance / (interpolatedCount + 1) * (i + 1);
        // eslint-disable-next-line max-len
        const formattedDistance = input[input.length - 1].formattedDistance / (interpolatedCount + 1) * (i + 1);

        while (input[inputIndex].distance < distance) {
            output.push(input[inputIndex++]);
        }

        const upperElevation = input[inputIndex].elevation;
        const lowerElevation = input[inputIndex - 1].elevation;
        const upperFormattedElevation = input[inputIndex].formattedElevation;
        const lowerFormattedElevation = input[inputIndex - 1].formattedElevation;
        const upperDistance = input[inputIndex].distance;
        const lowerDistance = input[inputIndex - 1].distance;
        const t = (distance - lowerDistance) / (upperDistance - lowerDistance);

        output.push({
            distance: distance,
            elevation: interpolateNumber(lowerElevation, upperElevation)(t),
            formattedDistance: formattedDistance,
            // eslint-disable-next-line max-len
            formattedElevation: interpolateNumber(lowerFormattedElevation, upperFormattedElevation)(t),
        });

        i++;
    }

    while (i < input.length) {
        output.push(input[i++]);
    }

    return output;
}

export default ElevationProfile;
